import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { optionLabel } from '@/components/classification/ClassifyPicker'
import type { Message } from '@/domain/message.types'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { translationGroupKey, useCreateMessage, useRequestTranslation, useTranslationGroup } from '@/hooks/useMessages'
import { sortedByLabel } from '@/lib/option-order'
import { buildRequestKey } from './request-key'

/**
 * The backend's translate endpoint treats its path-param id as THE source
 * unconditionally — it must always be the group's actual is_source row, not
 * whichever variant's page the user happens to be viewing. Viewing a
 * translated sibling's own page and requesting AI translation for another
 * locale must translate the canonical source content, not that sibling's
 * (already-translated) text, and must never target the source's own locale
 * by mistake.
 */
function resolveSourceMessageId(variants: Message[], message: Message | undefined): string {
	return variants.find((v) => v.isSource)?.messageId ?? message?.messageId ?? ''
}

/**
 * While the group's own variants are still loading, `usedLocales` would be
 * empty and every locale would look "available" — including ones already
 * in the group. Show no options yet rather than a wrong, briefly-clickable
 * list (this resolves on every page load, not just a rare race).
 */
function computeAvailableLocaleOptions(
	isVariantsLoading: boolean,
	localeOptions: { code: string; label: string }[],
	usedLocales: Set<string>,
): { value: string; label: string }[] {
	if (isVariantsLoading) return []
	return sortedByLabel(
		localeOptions
			.filter((o) => !usedLocales.has(o.code))
			.map((o) => ({ value: o.code, label: optionLabel(o.code, o.label) })),
	)
}

/** A new, empty (untranslated) target-locale row for `message`'s group. */
function buildAddLocaleInput(message: Message, locale: string) {
	return {
		title: message.title,
		content: '',
		status: message.status,
		languageIso: locale,
		messageType: message.messageType,
		translationGroupId: message.translationGroupId,
		isSource: false,
	}
}

/** Immutable add/remove on a request-key Set — used to track per-request in-flight state. */
function withRequestKey(set: ReadonlySet<string>, key: string, included: boolean): Set<string> {
	const next = new Set(set)
	if (included) next.add(key)
	else next.delete(key)
	return next
}

/** Immutable set-or-delete on a request-key Map — used to track per-request error state. */
function withRequestError(map: ReadonlyMap<string, string>, key: string, message: string | null): Map<string, string> {
	const next = new Map(map)
	if (message) next.set(key, message)
	else next.delete(key)
	return next
}

/**
 * Data + handlers for `MessageDetail.tsx`'s Translations section — split out
 * of the page component to keep it under the 100-line function limit
 * (CLAUDE.md), not because this is reused elsewhere yet.
 */
export function useTranslationsPanel(message: Message | undefined) {
	const qc = useQueryClient()
	const { data: variants = [], isLoading: isVariantsLoading } = useTranslationGroup(message?.translationGroupId ?? null)
	const { axes } = useAxesWithValues()
	const createMessage = useCreateMessage()
	const requestTranslation = useRequestTranslation(resolveSourceMessageId(variants, message))
	// A Set, not a single locale — translating two different locales in quick
	// succession must track each independently, or the second click's onSettled
	// would clear the first's still-in-flight busy state (and vice versa).
	const [requestingLocales, setRequestingLocales] = useState<ReadonlySet<string>>(new Set())
	// Per-request-key, not a single shared flag — `requestTranslation` is ONE
	// mutation instance, and its own `isError` reflects only whichever call
	// settled MOST RECENTLY (see the `mutateAsync` comment on `translate`
	// below for why the observer can't be trusted for per-call state at all).
	// A single shared error flag could flicker on/off or attribute a failure
	// to the wrong locale under two concurrent requests.
	const [translateErrors, setTranslateErrors] = useState<ReadonlyMap<string, string>>(new Map())

	// The page route (`/messages/:id`) doesn't remount on navigation between two
	// different messages — only `message` changes — so without this, requesting
	// a translation for one message and then following a locale link to another
	// message left this locale flagged "in flight" there too, since it's just a
	// bare locale string with no message/group scoping of its own. Keyed on
	// `messageId`, not `translationGroupId`: sibling locale links stay WITHIN
	// the same group, so a group-keyed reset would never fire for exactly the
	// same-group navigation this exists to handle.
	useEffect(() => {
		setRequestingLocales(new Set())
		setTranslateErrors(new Map())
	}, [message?.messageId])

	const localeAxis = axes.find((a) => a.code === 'locale')
	const usedLocales = new Set(variants.map((v) => v.languageIso))
	const availableLocaleOptions = computeAvailableLocaleOptions(
		isVariantsLoading,
		localeAxis?.options ?? [],
		usedLocales,
	)

	const addLocale = (locale: string) => {
		if (!message) return
		const translationGroupId = message.translationGroupId
		createMessage.mutate(buildAddLocaleInput(message, locale), {
			// useCreateMessage's own invalidation only covers the generic
			// ['messages']/['message'] keys (used by the list/detail pages) — it
			// has no idea the Translations section reads from a differently-keyed
			// ['translation-group', ...] query, so without this the new row was
			// invisible here until an unrelated refetch or a full page reload.
			onSuccess: () => void qc.invalidateQueries({ queryKey: translationGroupKey(translationGroupId) }),
		})
	}

	// `mutateAsync` (not `mutate`'s `onSettled` option): TanStack Query's
	// MutationObserver keeps ONE shared per-call-options field, which a second
	// `mutate()` call overwrites before the first settles — silently dropping
	// the first call's `onSettled`. The returned promise, in contrast, resolves
	// per-call regardless, so two concurrently-requested locales each clear
	// their own busy state correctly.
	const translate = (locale: string) => {
		if (!message) return
		const key = buildRequestKey(message.messageId, locale)
		setRequestingLocales((prev) => withRequestKey(prev, key, true))
		setTranslateErrors((prev) => withRequestError(prev, key, null)) // clear a stale error on retry
		requestTranslation
			.mutateAsync([locale])
			.catch(() =>
				setTranslateErrors((prev) => withRequestError(prev, key, 'Could not request the translation. Try again.')),
			)
			.finally(() => setRequestingLocales((prev) => withRequestKey(prev, key, false)))
	}

	return {
		variants,
		availableLocaleOptions,
		addLocale,
		isAddingLocale: createMessage.isPending,
		addLocaleError: createMessage.isError ? 'Could not add that locale. Try again.' : null,
		translate,
		requestingLocales,
		translateErrors,
	}
}
